import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { toValidObjectIdString, parseApiDate } from '../utils/security';
import {
  fetchRandevuBedelleri,
  fetchRandevuBedeliDetay,
  randevuBedeliOnayla,
  randevuBedeliIptal,
  hakedisOlusturFromBedeller,
  RANDEVU_BEDELI_SAYAC_YENILE_EVENT,
} from '../services/randevuBedeliService';
import { fetchHakedisDetay } from '../services/hakedisService';
import { useConfirmDialog } from '../contexts/ConfirmContext';
import RandevuBedeliOnayModal from '../components/RandevuBedeliOnayModal';
import TableListFooter from '../components/TableListFooter';
import RandevuBedeliDetayModal from '../components/RandevuBedeliDetayModal';
import HakedisOlusturModal from '../components/HakedisOlusturModal';
import HakedisDetayModal from '../components/HakedisDetayModal';

const PAGE_SIZE = 25;
const SEARCH_DEBOUNCE_MS = 400;

const DURUM_LABELS = {
  OnayBekliyor: 'Onay Bekliyor',
  Onaylandi: 'Onaylandı',
  HakediseAktarildi: 'Hakedişe Aktarıldı',
  IptalEdildi: 'İptal Edildi',
};

const formatCurrency = (amount) => {
  if (amount === null || amount === undefined) return '—';
  return `${parseFloat(amount).toFixed(2)} ₺`;
};

/** İlgili randevunun tarih ve saatini tek satırda gösterir (tarih sadece gün/ay/yıl, saat ayrı alan) */
const formatRandevuTarihSaat = (randevuTarihi, randevuSaati) => {
  const d = parseApiDate(randevuTarihi);
  if (!d || isNaN(d.getTime())) return '—';
  const dateStr = d.toLocaleDateString('tr-TR', { year: 'numeric', month: '2-digit', day: '2-digit' });
  const saat = randevuSaati != null && String(randevuSaati).trim() ? String(randevuSaati).trim() : '';
  return saat ? `${dateStr} ${saat}` : dateStr;
};

const getErrorMessage = (error, fallback) => {
  if (!error) return fallback;
  const msg = error?.message || error?.data?.mesaj || error?.data?.message || fallback;
  if (error?.status === 404) return 'Randevu bedelleri API’si bulunamadı (404). Backend’in güncel sürümle çalıştığından emin olun.';
  return msg === 'Failed to fetch' ? 'Sunucuya bağlanılamadı' : msg;
};

const DURUM_VALUES = ['', 'OnayBekliyor', 'Onaylandi', 'HakediseAktarildi', 'IptalEdildi'];

const RandevuBedelleriPage = () => {
  const { confirm } = useConfirmDialog();
  const [searchParams, setSearchParams] = useSearchParams();
  const urlInitRef = useRef(false);
  const durumFromUrl = searchParams.get('durum') || '';
  const initialDurum = DURUM_VALUES.includes(durumFromUrl) ? durumFromUrl : '';
  const aramaFromUrl = searchParams.get('arama') || '';

  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchInput, setSearchInput] = useState(aramaFromUrl);
  const [debouncedSearch, setDebouncedSearch] = useState(aramaFromUrl);
  const [durumFilter, setDurumFilter] = useState(initialDurum);
  const [error, setError] = useState('');
  const [onayModal, setOnayModal] = useState({ open: false, bedel: null });
  const [detayModalBedel, setDetayModalBedel] = useState(null);
  const [hakedisOlusturOpen, setHakedisOlusturOpen] = useState(false);
  const [hakedisModalPrefill, setHakedisModalPrefill] = useState(null);
  const [selectedHakedis, setSelectedHakedis] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [paginationMeta, setPaginationMeta] = useState({
    total: 0,
    totalPages: 1,
  });

  useEffect(() => {
    const t = setTimeout(() => {
      setDebouncedSearch(searchInput.trim());
      setSearchParams((prev) => {
        const next = new URLSearchParams(prev);
        if (searchInput.trim()) next.set('arama', searchInput.trim());
        else next.delete('arama');
        return next;
      }, { replace: true });
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [searchInput, setSearchParams]);

  useEffect(() => {
    if (urlInitRef.current) return;
    urlInitRef.current = true;
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      if (initialDurum) next.set('durum', initialDurum);
      if (aramaFromUrl) next.set('arama', aramaFromUrl);
      return next;
    }, { replace: true });
  }, [initialDurum, aramaFromUrl, setSearchParams]);

  const effectiveArama = debouncedSearch.length >= 3 ? debouncedSearch : undefined;

  useEffect(() => {
    setCurrentPage(1);
  }, [debouncedSearch, durumFilter]);

  useEffect(() => {
    if (paginationMeta.totalPages >= 1 && currentPage > paginationMeta.totalPages) {
      setCurrentPage(paginationMeta.totalPages);
    }
  }, [paginationMeta.totalPages, currentPage]);

  const loadList = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchRandevuBedelleri({
        sayfa: currentPage,
        limit: PAGE_SIZE,
        arama: effectiveArama,
        durum: durumFilter || undefined,
      });
      if (res?.basarili && res?.veri) {
        setList(res.veri.randevuBedelleri || []);
        const p = res.veri.sayfalama || {};
        setPaginationMeta({
          total: p.toplam ?? 0,
          totalPages: Math.max(1, p.toplamSayfa ?? 1),
        });
      } else {
        setList([]);
        setPaginationMeta({ total: 0, totalPages: 1 });
      }
    } catch (err) {
      setError(getErrorMessage(err, 'Randevu bedelleri yüklenemedi'));
      setList([]);
      setPaginationMeta({ total: 0, totalPages: 1 });
    } finally {
      setLoading(false);
    }
  }, [effectiveArama, durumFilter, currentPage]);

  useEffect(() => {
    loadList();
  }, [loadList]);

  const refreshSidebarCounts = () => {
    window.dispatchEvent(new Event(RANDEVU_BEDELI_SAYAC_YENILE_EVENT));
  };

  const handleOnayla = async (bedel) => {
    setOnayModal({ open: true, bedel });
  };

  const handleOnaySubmit = async (payload) => {
    const id = toValidObjectIdString(onayModal.bedel?.id ?? onayModal.bedel?._id);
    if (!id) return;
    setActionLoading(id);
    try {
      const res = await randevuBedeliOnayla(id, payload);
      if (res?.basarili) {
        window.showNotification?.('success', 'Randevu bedeli onaylandı');
        setOnayModal({ open: false, bedel: null });
        loadList();
        refreshSidebarCounts();
      } else {
        window.showNotification?.('error', res?.mesaj || 'Onaylama başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Onaylama başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleIptal = async (rb) => {
    const id = toValidObjectIdString(rb?.id ?? rb?._id);
    if (!id) return;
    const ok = await confirm({
      title: 'Randevu bedelini iptal et',
      message: 'Bu randevu bedeli talebini iptal etmek istediğinize emin misiniz? Onay ve hakediş işlemleri yapılmaz.',
      variant: 'danger',
      confirmLabel: 'Evet, iptal et',
    });
    if (!ok) return;
    setActionLoading(id);
    try {
      const res = await randevuBedeliIptal(id);
      if (res?.basarili) {
        window.showNotification?.('success', 'Randevu bedeli iptal edildi');
        loadList();
        refreshSidebarCounts();
      } else {
        window.showNotification?.('error', res?.mesaj || 'İptal başarısız');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'İptal başarısız'));
    } finally {
      setActionLoading(null);
    }
  };

  const openHakedisModal = (prefill = null) => {
    setHakedisModalPrefill(prefill);
    setHakedisOlusturOpen(true);
  };

  const closeHakedisModal = () => {
    setHakedisOlusturOpen(false);
    setHakedisModalPrefill(null);
  };

  const handleHakedisOlusturFromRow = async (rb) => {
    const id = toValidObjectIdString(rb?.id ?? rb?._id);
    const teknikServisId = toValidObjectIdString(rb?.teknikServisId);
    if (!id || !teknikServisId) return;

    const ok = await confirm({
      title: 'Hakediş oluştur',
      message: 'Hakediş oluşturmak istiyor musunuz?',
      variant: 'primary',
      confirmLabel: 'Evet',
      cancelLabel: 'Hayır',
      iconClass: 'fas fa-file-invoice-dollar',
      confirmButtonClassName: 'primary-btn',
    });
    if (!ok) return;

    openHakedisModal({
      teknikServis: {
        id: teknikServisId,
        ad: rb.teknikServisAdi || '',
      },
      selectedIds: [id],
    });
  };

  const handleViewHakedis = async (rb) => {
    const hakedisId = toValidObjectIdString(rb?.hakedisId);
    if (!hakedisId) {
      window.showNotification?.('error', 'Hakediş bilgisi bulunamadı');
      return;
    }
    const rowId = rb.id || rb._id;
    setActionLoading(`hakedis-view-${rowId}`);
    try {
      const response = await fetchHakedisDetay(hakedisId);
      if (response?.basarili && response?.veri?.hakedis) {
        setSelectedHakedis(response.veri.hakedis);
      } else {
        window.showNotification?.('error', response?.mesaj || 'Hakediş yüklenemedi');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Hakediş yüklenemedi'));
    } finally {
      setActionLoading(null);
    }
  };

  const handleHakedisOlustur = async (selectedIds) => {
    if (!selectedIds || selectedIds.length === 0) {
      window.showNotification?.('error', 'En az bir randevu bedeli seçin');
      return;
    }
    setActionLoading('hakedis');
    try {
      const res = await hakedisOlusturFromBedeller(selectedIds);
      if (res?.basarili) {
        window.showNotification?.('success', 'Hakediş oluşturuldu');
        closeHakedisModal();
        loadList();
        refreshSidebarCounts();
      } else {
        window.showNotification?.('error', res?.mesaj || 'Hakediş oluşturulamadı');
      }
    } catch (err) {
      window.showNotification?.('error', getErrorMessage(err, 'Hakediş oluşturulamadı'));
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <main className="main-content">
      <div className="content-header">
        <h1>Randevu Bedelleri</h1>
      </div>

      <div className="admin-finance-toolbar">
        <div className="admin-finance-search-row">
          <div className="search-box admin-finance-search-box">
            <i className="fas fa-search" aria-hidden="true"></i>
            <input
              type="text"
              placeholder="Randevu no veya teknik servis (en az 3 karakter)…"
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              maxLength={80}
              aria-label="Ara"
            />
            {loading && searchInput.trim().length >= 3 && (
              <i className="fas fa-spinner fa-spin admin-finance-search-spinner" aria-hidden="true"></i>
            )}
          </div>
          <select
            className="admin-finance-select"
            value={durumFilter}
            onChange={(e) => {
              const v = e.target.value;
              setDurumFilter(v);
              setSearchParams((prev) => {
                const next = new URLSearchParams(prev);
                if (v) next.set('durum', v);
                else next.delete('durum');
                return next;
              }, { replace: true });
            }}
            aria-label="Durum filtresi"
          >
            <option value="">Tümü</option>
            <option value="OnayBekliyor">Onay Bekliyor</option>
            <option value="Onaylandi">Onaylandı</option>
            <option value="HakediseAktarildi">Hakedişe Aktarıldı</option>
            <option value="IptalEdildi">İptal Edildi</option>
          </select>
          <button type="button" className="primary-btn" onClick={() => openHakedisModal(null)}>
            <i className="fas fa-plus"></i> Hakediş oluştur
          </button>
        </div>
        <p className="admin-finance-search-hint">
          Arama en az 3 karakter sonrası uygulanır. Sayfa başına en fazla {PAGE_SIZE} kayıt.
        </p>
      </div>

      <div className="card">
        {loading && list.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center' }}>Yükleniyor...</div>
        ) : error ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--error)' }}>{error}</div>
        ) : list.length === 0 ? (
          <div style={{ padding: '40px', textAlign: 'center', color: 'var(--text-gray)' }}>Randevu bedeli bulunamadı</div>
        ) : (
          <>
            <div className="table-container">
              <table className="admins-table">
                <thead>
                  <tr>
                    <th>Randevu No</th>
                    <th>Teknik Servis</th>
                    <th>Tarih / Saat</th>
                    <th style={{ textAlign: 'right' }}>Toplam</th>
                    <th>Durum</th>
                    <th className="islemler-sutunu">İşlem</th>
                  </tr>
                </thead>
                <tbody>
                  {list.map((rb) => {
                    const randevuLinkHref = rb.servisKaydiId && rb.randevuId ? `/service-records/${rb.servisKaydiId}/appointments/${rb.randevuId}` : null;
                    const rowId = rb.id || rb._id;
                    return (
                      <tr key={rowId}>
                        <td style={{ fontWeight: 500 }}>
                          {randevuLinkHref ? (
                            <Link to={randevuLinkHref} style={{ color: 'var(--primary, #2563eb)', textDecoration: 'none', fontWeight: 500 }} title="Randevu detayı">
                              {rb.randevuNumarasi || '—'}
                            </Link>
                          ) : (
                            rb.randevuNumarasi || '—'
                          )}
                        </td>
                        <td>{rb.teknikServisAdi || '—'}</td>
                        <td>{formatRandevuTarihSaat(rb.randevuTarihi ?? rb.olusturmaTarihi, rb.randevuSaati)}</td>
                        <td style={{ textAlign: 'right' }}>{formatCurrency(rb.toplamTutar)}</td>
                        <td>{DURUM_LABELS[rb.durum] || rb.durum || '—'}</td>
                        <td className="table-actions islemler-sutunu">
                          <div style={{ display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'nowrap' }}>
                            <button
                              type="button"
                              className="category-action-btn edit-btn icon-only-btn"
                              onClick={() => setDetayModalBedel(rb)}
                              title="Bedel detayı"
                              aria-label="Bedel detayı"
                            >
                              <i className="fas fa-eye"></i>
                            </button>
                            {rb.durum === 'OnayBekliyor' && (
                              <>
                                <button
                                  type="button"
                                  className="category-action-btn edit-btn icon-only-btn"
                                  onClick={() => handleOnayla(rb)}
                                  title="Onayla"
                                  aria-label="Onayla"
                                  disabled={actionLoading === rowId}
                                >
                                  <i className="fas fa-check"></i>
                                </button>
                                <button
                                  type="button"
                                  className="category-action-btn delete-btn icon-only-btn"
                                  onClick={() => handleIptal(rb)}
                                  title="İptal et"
                                  aria-label="İptal et"
                                  disabled={actionLoading === rowId}
                                >
                                  <i className="fas fa-times"></i>
                                </button>
                              </>
                            )}
                            {rb.durum === 'Onaylandi' && (
                              <button
                                type="button"
                                className="category-action-btn edit-btn icon-only-btn"
                                onClick={() => handleHakedisOlusturFromRow(rb)}
                                title="Hakediş oluştur"
                                aria-label="Hakediş oluştur"
                                disabled={actionLoading === 'hakedis'}
                              >
                                <i className="fas fa-plus"></i>
                              </button>
                            )}
                            {rb.durum === 'HakediseAktarildi' && rb.hakedisId && (
                              <button
                                type="button"
                                className="category-action-btn hakedis-btn icon-only-btn"
                                onClick={() => handleViewHakedis(rb)}
                                title="Hakedişi göster"
                                aria-label="Hakedişi göster"
                                disabled={actionLoading === `hakedis-view-${rowId}`}
                              >
                                <i className="fas fa-file-invoice-dollar"></i>
                              </button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
            <TableListFooter
              totalRecords={paginationMeta.total}
              currentPage={currentPage}
              totalPages={paginationMeta.totalPages || 1}
              onPageChange={setCurrentPage}
              loading={loading}
              embedded
            />
          </>
        )}
      </div>

      {onayModal.open && onayModal.bedel && (
        <RandevuBedeliOnayModal
          bedel={onayModal.bedel}
          onClose={() => setOnayModal({ open: false, bedel: null })}
          onSubmit={handleOnaySubmit}
          loading={!!actionLoading}
          fetchDetay={fetchRandevuBedeliDetay}
        />
      )}

      {detayModalBedel && (
        <RandevuBedeliDetayModal
          bedel={detayModalBedel}
          onClose={() => setDetayModalBedel(null)}
          fetchDetay={fetchRandevuBedeliDetay}
        />
      )}

      {hakedisOlusturOpen && (
        <HakedisOlusturModal
          onClose={closeHakedisModal}
          onSubmit={handleHakedisOlustur}
          loading={!!actionLoading}
          initialTeknikServis={hakedisModalPrefill?.teknikServis ?? null}
          initialSelectedIds={hakedisModalPrefill?.selectedIds ?? []}
        />
      )}

      {selectedHakedis && (
        <HakedisDetayModal
          hakedis={selectedHakedis}
          onClose={() => setSelectedHakedis(null)}
          onRefresh={() => {
            loadList();
            refreshSidebarCounts();
          }}
        />
      )}
    </main>
  );
};

export default RandevuBedelleriPage;
